/**
 * EZ Pull It — Vehicle Alerts page JS
 * Phone-based flow — no login required.
 *
 * Step 1: Customer enters phone number → worker looks them up
 * Step 2a: New customer — show contact fields + empty vehicle row
 * Step 2b: Returning customer — show existing vehicles pre-populated
 * Save: POST to worker, which creates/updates the Shopify customer record
 */

(function () {
  'use strict';

  const workerUrl = (window.__VP_WORKER_URL__ || '').replace(/\/$/, '');
  const carData   = window.__VP_CAR_DATA__ || {};

  // ── Translations ────────────────────────────────────────────────────────────
  const i18n = {
    en: {
      heroHeading: "Be the first to know when your car arrives",
      heroSub: 'Tell us which vehicles you\'re looking for and we\'ll send you an <strong>instant SMS text message</strong> the moment that car lands in our yard — so you can get there before anyone else.',
      benefitAlerts: "Instant SMS Alerts",
      benefitAlertsSub: "Get a text the moment your vehicle is available — no checking the website required.",
      benefitVehicles: "Up to 10 Vehicles",
      benefitVehiclesSub: "Watch for multiple makes and models at the same time.",
      benefitRush: "Beat the Rush",
      benefitRushSub: "Popular parts go fast. Alerts give you a head start before the car hits the floor.",
      benefitUpdate: "Easy to Update",
      benefitUpdateSub: "Add or remove vehicles from your alert list anytime — right here on this page.",
      phoneHeading: "Enter your mobile number to get started",
      phoneSub: "We'll use your phone number to send alerts and to look up any existing alerts you've set up.",
      phonePlaceholder: "(509) 555-1234",
      phoneBtn: "Look Up My Alerts",
      phoneLooking: "Looking up…",
      phoneError: "Please enter your mobile number.",
      phoneServerError: "Something went wrong. Please try again.",
      newHeading: "Set up your vehicle alerts",
      newSub: "No alerts on file for that number. Fill in your details below to get started.",
      returnHeading: "Your vehicle alerts",
      returnSub: "Here are the vehicles you're currently watching. Add, remove, or update them below.",
      firstName: "First name",
      lastName: "Last name",
      email: "Email address",
      emailHint: "(optional — for email alerts too)",
      optional: "(optional)",
      required: "*",
      selectMake: "Select Make",
      selectModel: "Select Model",
      addVehicle: "+ Add a Vehicle",
      smsConsentText: 'I agree to receive SMS text alerts from EZ Pull It when vehicles I\'m watching arrive. Message & data rates may apply. Reply STOP to unsubscribe at any time.',
      emailConsentText: "Also send me email alerts when my vehicles arrive.",
      saveBtn: "Save My Alerts",
      saving: "Saving…",
      backBtn: "← Change phone number",
      errorFirstName: "Please enter your first name.",
      errorConsent: "Please check the SMS consent box to continue.",
      errorIncomplete: "Please complete both Make and Model for each vehicle, or remove incomplete rows.",
      errorGeneric: "Something went wrong. Please try again.",
      successCleared: "✅ All vehicle alerts have been removed. You won't receive any more SMS alerts.",
      successSaved: "✅ Your vehicle alerts have been saved! You'll get an SMS text as soon as one of these vehicles arrives.",
    },
    es: {
      heroHeading: "Sé el primero en saber cuando llega tu auto",
      heroSub: 'Dinos qué vehículos buscas y te enviaremos un <strong>mensaje de texto SMS instantáneo</strong> en el momento en que ese auto llegue a nuestro yonke — para que llegues antes que nadie.',
      benefitAlerts: "Alertas SMS Instantáneas",
      benefitAlertsSub: "Recibe un texto en el momento en que tu vehículo esté disponible — sin necesidad de revisar el sitio web.",
      benefitVehicles: "Hasta 10 Vehículos",
      benefitVehiclesSub: "Busca múltiples marcas y modelos al mismo tiempo.",
      benefitRush: "Gana la Carrera",
      benefitRushSub: "Las partes populares se van rápido. Las alertas te dan ventaja antes de que el auto llegue al piso.",
      benefitUpdate: "Fácil de Actualizar",
      benefitUpdateSub: "Agrega o quita vehículos de tu lista de alertas cuando quieras — aquí mismo en esta página.",
      phoneHeading: "Ingresa tu número de celular para comenzar",
      phoneSub: "Usaremos tu número de teléfono para enviar alertas y buscar alertas que ya hayas configurado.",
      phonePlaceholder: "(509) 555-1234",
      phoneBtn: "Buscar Mis Alertas",
      phoneLooking: "Buscando…",
      phoneError: "Por favor ingresa tu número de celular.",
      phoneServerError: "Algo salió mal. Por favor intenta de nuevo.",
      newHeading: "Configura tus alertas de vehículos",
      newSub: "No hay alertas para ese número. Completa tus datos a continuación para comenzar.",
      returnHeading: "Tus alertas de vehículos",
      returnSub: "Estos son los vehículos que estás rastreando. Agrega, quita o actualiza a continuación.",
      firstName: "Nombre",
      lastName: "Apellido",
      email: "Correo electrónico",
      emailHint: "(opcional — para alertas por email también)",
      optional: "(opcional)",
      required: "*",
      selectMake: "Seleccionar Marca",
      selectModel: "Seleccionar Modelo",
      addVehicle: "+ Agregar un Vehículo",
      smsConsentText: 'Acepto recibir alertas de texto SMS de EZ Pull It cuando los vehículos que estoy buscando lleguen. Pueden aplicar tarifas de mensajes y datos. Responde STOP para cancelar en cualquier momento.',
      emailConsentText: "También envíame alertas por email cuando lleguen mis vehículos.",
      saveBtn: "Guardar Mis Alertas",
      saving: "Guardando…",
      backBtn: "← Cambiar número de teléfono",
      errorFirstName: "Por favor ingresa tu nombre.",
      errorConsent: "Por favor marca la casilla de consentimiento de SMS para continuar.",
      errorIncomplete: "Por favor completa Marca y Modelo para cada vehículo, o elimina las filas incompletas.",
      errorGeneric: "Algo salió mal. Por favor intenta de nuevo.",
      successCleared: "✅ Todas las alertas de vehículos han sido eliminadas. No recibirás más alertas por SMS.",
      successSaved: "✅ ¡Tus alertas de vehículos han sido guardadas! Recibirás un SMS tan pronto como uno de estos vehículos llegue.",
    },
  };

  // ── DOM refs ────────────────────────────────────────────────────────────────
  const stepPhone      = document.getElementById('vp-step-phone');
  const stepForm       = document.getElementById('vp-step-form');
  const phoneInput     = document.getElementById('vp-phone-input');
  const phoneBtn       = document.getElementById('vp-phone-btn');
  const phoneError     = document.getElementById('vp-phone-error');
  const newIntro       = document.getElementById('vp-new-intro');
  const returningIntro = document.getElementById('vp-returning-intro');
  const contactFields  = document.getElementById('vp-contact-fields');
  const firstNameInput = document.getElementById('vp-first-name');
  const lastNameInput  = document.getElementById('vp-last-name');
  const emailInput     = document.getElementById('vp-email');
  const rowsContainer  = document.getElementById('vp-rows');
  const addBtn         = document.getElementById('vp-add-btn');
  const smsConsent     = document.getElementById('vp-sms-consent');
  const emailConsent   = document.getElementById('vp-email-consent');
  const saveBtn        = document.getElementById('vp-save-btn');
  const backBtn        = document.getElementById('vp-back-btn');
  const formError      = document.getElementById('vp-form-error');
  const feedback       = document.getElementById('vp-feedback');

  // State
  let currentPhone    = '';
  let currentCustomerId = null;
  let isNewCustomer   = true;
  let currentLang     = 'en';

  // ── Language toggle ──────────────────────────────────────────────────────────

  const langBtnEn = document.getElementById('vp-lang-en');
  const langBtnEs = document.getElementById('vp-lang-es');

  function setLang(lang) {
    currentLang = lang;
    const t = i18n[lang];

    // Toggle button active state
    langBtnEn.classList.toggle('vp-lang-btn--active', lang === 'en');
    langBtnEs.classList.toggle('vp-lang-btn--active', lang === 'es');

    // Hero section
    const heroHeading = document.querySelector('.vp-hero__heading');
    const heroSub = document.querySelector('.vp-hero__sub');
    if (heroHeading) heroHeading.textContent = t.heroHeading;
    if (heroSub) heroSub.innerHTML = t.heroSub;

    // Benefits
    const benefits = document.querySelectorAll('.vp-benefit');
    const benefitKeys = ['benefitAlerts', 'benefitVehicles', 'benefitRush', 'benefitUpdate'];
    const benefitSubKeys = ['benefitAlertsSub', 'benefitVehiclesSub', 'benefitRushSub', 'benefitUpdateSub'];
    benefits.forEach(function (el, i) {
      const strong = el.querySelector('strong');
      const p = el.querySelector('p');
      if (strong && benefitKeys[i]) strong.textContent = t[benefitKeys[i]];
      if (p && benefitSubKeys[i]) p.textContent = t[benefitSubKeys[i]];
    });

    // Step 1: Phone
    const phoneHeading = stepPhone.querySelector('.vp-step__heading');
    const phoneSub = stepPhone.querySelector('.vp-step__sub');
    if (phoneHeading) phoneHeading.textContent = t.phoneHeading;
    if (phoneSub) phoneSub.textContent = t.phoneSub;
    phoneInput.placeholder = t.phonePlaceholder;
    if (!phoneBtn.disabled) phoneBtn.textContent = t.phoneBtn;

    // Step 2: Intros
    const newH = newIntro.querySelector('.vp-step__heading');
    const newS = newIntro.querySelector('.vp-step__sub');
    if (newH) newH.textContent = t.newHeading;
    if (newS) newS.textContent = t.newSub;

    const retH = returningIntro.querySelector('.vp-step__heading');
    const retS = returningIntro.querySelector('.vp-step__sub');
    if (retH) retH.textContent = t.returnHeading;
    if (retS) retS.textContent = t.returnSub;

    // Contact fields labels
    const firstLabel = document.querySelector('label[for="vp-first-name"]');
    const lastLabel = document.querySelector('label[for="vp-last-name"]');
    const emailLabel = document.querySelector('label[for="vp-email"]');
    if (firstLabel) firstLabel.innerHTML = `${t.firstName} <span class="vp-required">${t.required}</span>`;
    if (lastLabel) lastLabel.innerHTML = `${t.lastName} <span class="vp-optional">${t.optional}</span>`;
    if (emailLabel) emailLabel.innerHTML = `${t.email} <span class="vp-optional">${t.emailHint}</span>`;
    firstNameInput.placeholder = t.firstName;
    lastNameInput.placeholder = t.lastName;

    // Add vehicle button
    addBtn.textContent = t.addVehicle;

    // Consent labels
    const smsLabel = smsConsent.parentElement.querySelector('span');
    const emailLabelConsent = emailConsent.parentElement.querySelector('span');
    if (smsLabel) smsLabel.innerHTML = `${t.smsConsentText} <span class="vp-required">${t.required}</span>`;
    if (emailLabelConsent) emailLabelConsent.innerHTML = `${t.emailConsentText} <span class="vp-optional">${t.optional}</span>`;

    // Buttons
    if (!saveBtn.disabled) saveBtn.textContent = t.saveBtn;
    backBtn.textContent = t.backBtn;

    // Update make/model placeholders in existing rows
    rowsContainer.querySelectorAll('.vp-make-select').forEach(function (sel) {
      if (sel.options[0] && sel.options[0].value === '') sel.options[0].textContent = t.selectMake;
    });
    rowsContainer.querySelectorAll('.vp-model-select').forEach(function (sel) {
      if (sel.options[0] && sel.options[0].value === '') sel.options[0].textContent = t.selectModel;
    });
  }

  langBtnEn.addEventListener('click', function () { setLang('en'); });
  langBtnEs.addEventListener('click', function () { setLang('es'); });

  // ── Step 1: Phone lookup ─────────────────────────────────────────────────────

  phoneBtn.addEventListener('click', lookupPhone);
  phoneInput.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') lookupPhone();
  });

  async function lookupPhone() {
    const raw = phoneInput.value.trim();
    if (!raw) {
      showPhoneError(i18n[currentLang].phoneError);
      return;
    }

    phoneBtn.disabled = true;
    phoneBtn.textContent = i18n[currentLang].phoneLooking;
    hidePhoneError();

    try {
      const resp = await fetch(`${workerUrl}/api/alerts-by-phone?phone=${encodeURIComponent(raw)}`);
      if (!resp.ok) throw new Error(`Server error ${resp.status}`);
      const data = await resp.json();

      currentPhone = raw;
      currentCustomerId = data.customerId;
      isNewCustomer = !data.found;

      // Transition to step 2
      stepPhone.hidden = true;
      stepForm.hidden  = false;

      if (data.found) {
        // Returning customer
        returningIntro.hidden = false;
        contactFields.hidden  = true;
        // Pre-set language if saved
        if (data.lang === 'es') setLang('es');
        // Pre-check consent boxes (they already consented)
        smsConsent.checked   = data.smsConsent;
        emailConsent.checked = data.emailConsent;
        // Pre-populate vehicles
        renderRows(data.vehicles.length > 0 ? data.vehicles : [{ make: '', model: '' }]);
        // Pre-fill existing vehicles
        if (data.vehicles.length > 0) {
          data.vehicles.forEach(function (v, i) {
            const rows = rowsContainer.querySelectorAll('.vp-row');
            if (rows[i]) {
              const makeSelect  = rows[i].querySelector('.vp-make-select');
              const modelSelect = rows[i].querySelector('.vp-model-select');
              if (makeSelect) {
                makeSelect.value = v.make;
                populateModels(modelSelect, v.make, v.model);
              }
            }
          });
        }
      } else {
        // New customer
        newIntro.hidden      = false;
        contactFields.hidden = false;
        smsConsent.checked   = false;
        emailConsent.checked = false;
        renderRows([{ make: '', model: '' }]);
      }

    } catch (err) {
      showPhoneError(i18n[currentLang].phoneServerError);
      console.error('Phone lookup error:', err);
    } finally {
      phoneBtn.disabled    = false;
      phoneBtn.textContent = i18n[currentLang].phoneBtn;
    }
  }

  // ── Back button ──────────────────────────────────────────────────────────────

  backBtn.addEventListener('click', function () {
    stepForm.hidden  = true;
    stepPhone.hidden = false;
    newIntro.hidden      = true;
    returningIntro.hidden = true;
    contactFields.hidden  = true;
    rowsContainer.innerHTML = '';
    hideFormError();
    hideFeedback();
    currentPhone = '';
    currentCustomerId = null;
  });

  // ── Vehicle rows ─────────────────────────────────────────────────────────────

  function renderRows(vehicles) {
    rowsContainer.innerHTML = '';
    vehicles.forEach(function (v) {
      addRow(v.make, v.model);
    });
  }

  function addRow(make, model) {
    const existing = rowsContainer.querySelectorAll('.vp-row').length;
    if (existing >= 10) return;

    const row = document.createElement('div');
    row.className = 'vp-row';

    // Make select
    const makeSelect = document.createElement('select');
    makeSelect.className = 'vp-make-select';
    makeSelect.setAttribute('aria-label', 'Vehicle make');

    const makePlaceholder = document.createElement('option');
    makePlaceholder.value = '';
    makePlaceholder.textContent = i18n[currentLang].selectMake;
    makeSelect.appendChild(makePlaceholder);

    Object.keys(carData).sort().forEach(function (m) {
      const opt = document.createElement('option');
      opt.value = m;
      opt.textContent = m;
      if (m === make) opt.selected = true;
      makeSelect.appendChild(opt);
    });

    // Model select
    const modelSelect = document.createElement('select');
    modelSelect.className = 'vp-model-select';
    modelSelect.setAttribute('aria-label', 'Vehicle model');

    populateModels(modelSelect, make, model);

    makeSelect.addEventListener('change', function () {
      populateModels(modelSelect, makeSelect.value, '');
    });

    // Remove button
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'vp-remove-btn';
    removeBtn.setAttribute('aria-label', 'Remove vehicle');
    removeBtn.textContent = '×';
    removeBtn.addEventListener('click', function () {
      row.remove();
    });

    row.appendChild(makeSelect);
    row.appendChild(modelSelect);
    row.appendChild(removeBtn);
    rowsContainer.appendChild(row);
  }

  function populateModels(modelSelect, make, selectedModel) {
    modelSelect.innerHTML = '';

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = i18n[currentLang].selectModel;
    modelSelect.appendChild(placeholder);

    const models = carData[make] || [];
    models.forEach(function (m) {
      const opt = document.createElement('option');
      opt.value = m;
      opt.textContent = m;
      if (m === selectedModel) opt.selected = true;
      modelSelect.appendChild(opt);
    });
  }

  addBtn.addEventListener('click', function () {
    addRow('', '');
  });

  // ── Save ─────────────────────────────────────────────────────────────────────

  saveBtn.addEventListener('click', saveAlerts);

  async function saveAlerts() {
    hideFormError();
    hideFeedback();

    // Validate first name for new customers
    if (isNewCustomer && !firstNameInput.value.trim()) {
      showFormError(i18n[currentLang].errorFirstName);
      firstNameInput.focus();
      return;
    }

    // Validate SMS consent
    if (!smsConsent.checked) {
      showFormError(i18n[currentLang].errorConsent);
      smsConsent.focus();
      return;
    }

    // Collect vehicles
    const rows = rowsContainer.querySelectorAll('.vp-row');
    const vehicles = [];
    let hasBlank = false;

    rows.forEach(function (row) {
      const make  = row.querySelector('.vp-make-select')?.value || '';
      const model = row.querySelector('.vp-model-select')?.value || '';
      if (make && model) {
        vehicles.push({ make, model });
      } else if (make || model) {
        hasBlank = true;
      }
    });

    if (hasBlank) {
      showFormError(i18n[currentLang].errorIncomplete);
      return;
    }

    // Allow zero vehicles — saving with none clears all alerts

    saveBtn.disabled    = true;
    saveBtn.textContent = i18n[currentLang].saving;

    try {
      const payload = {
        phone:        currentPhone,
        firstName:    isNewCustomer ? firstNameInput.value.trim() : undefined,
        lastName:     isNewCustomer ? lastNameInput.value.trim() : undefined,
        email:        isNewCustomer ? emailInput.value.trim() : undefined,
        vehicles,
        smsConsent:   smsConsent.checked,
        emailConsent: emailConsent.checked,
        lang:         currentLang,
      };

      const resp = await fetch(`${workerUrl}/api/alerts-by-phone`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${resp.status}`);
      }

      const data = await resp.json();

      // Success!
      const t = i18n[currentLang];
      const successMsg = vehicles.length === 0
        ? t.successCleared
        : t.successSaved;
      showFeedback(successMsg, 'success');

      // Update state so repeat saves work correctly
      currentCustomerId = data.customerId;
      isNewCustomer = false;
      returningIntro.hidden = false;
      newIntro.hidden       = true;
      contactFields.hidden  = true;

    } catch (err) {
      showFormError(i18n[currentLang].errorGeneric);
      console.error('Save error:', err);
    } finally {
      saveBtn.disabled    = false;
      saveBtn.textContent = i18n[currentLang].saveBtn;
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  function showPhoneError(msg) {
    phoneError.textContent = msg;
    phoneError.hidden = false;
  }
  function hidePhoneError() {
    phoneError.hidden = true;
    phoneError.textContent = '';
  }
  function showFormError(msg) {
    formError.textContent = msg;
    formError.hidden = false;
  }
  function hideFormError() {
    formError.hidden = true;
    formError.textContent = '';
  }
  function showFeedback(msg, type) {
    feedback.textContent = msg;
    feedback.className   = `vp-feedback vp-feedback--${type}`;
    feedback.hidden      = false;
  }
  function hideFeedback() {
    feedback.hidden = true;
    feedback.className = 'vp-feedback';
  }

})();
